import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHECK_METHOD_DEFS,
  MAX_DEDUCTION_RULES,
  WEEKDAY_DEFS,
  minutesOfTime,
  overlappingRules,
  scheduleHours,
  weeklyHours,
  type CheckMethod,
  type DeductionRule,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  Stat,
} from '../../../components/ui';
import { Icon } from '../../../components/Icon';
import { useToast } from '../../../components/Toast';
import { api } from '../../../lib/api';
import { num, plural, WORDS } from '../../../lib/format';
import { useAuth } from '../../../auth/AuthProvider';

export interface PlanRow {
  id: string;
  name: string;
  workDays: number[];
  startTime: string;
  endTime: string;
  breakMinutes: number;
  graceMinutes: number;
  flexibleMinutes: number;
  breakStart: string | null;
  breakEnd: string | null;
  overnight: boolean;

  absenceDeductionDays: number;
  forgotCheckoutDays: number;
  overtimeRate: number;
  holidayRate: number;
  lateRules: DeductionRule[];
  earlyRules: DeductionRule[];

  methods: CheckMethod[];
  requireGps: boolean;

  isDefault: boolean;
  isActive: boolean;
  employeeCount: number;
  dailyHours: number;
  weeklyHours: number;
}

interface SetupSummary {
  units: number;
  costCenters: number;
  schedules: number;
  headcount: number;
  withoutUnit: number;
  withoutSchedule: number;
  sites: number;
}

/** ما يردّه الخادمُ حين تُخالَف قاعدةُ «النهاية بعد البداية» — يُقال قبل الإرسال. */
const OVERNIGHT_SERVER_MESSAGE =
  'نهاية الوردية قبل بدايتها — علّمها «تعبر منتصف الليل» إن كانت كذلك.';

/**
 * خطط العمل.
 *
 * ## ولماذا الورديّةُ واللائحةُ في ورقةٍ واحدة
 *
 * كانتا شاشتين في أنظمةٍ كثيرة: «الورديّات» تُضبط في مكانٍ و«الجزاءات» في
 * مكانٍ آخر. وهما شيءٌ واحد: «من تأخّر نصفَ ساعة» لا معنى لها بلا موعدِ
 * بدءٍ تُقاس عليه، و«يبدأ التاسعة» لا أثرَ لها بلا لائحةٍ تقول ما يقع لمن
 * تأخّر. وفصلُهما يُنتج لائحةً تقيس على موعدٍ لا تعرفه.
 *
 * ## ولماذا لكلِّ موظّفٍ خطّتُه
 *
 * ساعةٌ واحدةٌ تُفرض على الشركة كلّها تكذب على نصفها: من يفتح المعرض
 * الثانية ظهرًا ليس متأخّرًا خمسَ ساعات، ومن يعمل الوردّيةَ الليليّةَ لا
 * يُحسب له من الثامنة صباحًا.
 */
export function WorkPlansSection() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const [editing, setEditing] = useState<PlanRow | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'setup', 'schedules'],
    queryFn: () => api<PlanRow[]>('/hr/setup/schedules'),
  });

  /* الملخّصُ وحدَه يعرف من لا خطّةَ له: قائمةُ الخطط تعدّ من فيها لا من خارجها. */
  const summary = useQuery({
    queryKey: ['hr', 'setup', 'summary'],
    queryFn: () => api<SetupSummary>('/hr/setup/summary'),
  });

  const rows = list.data ?? [];
  const active = rows.filter((s) => s.isActive);
  const stopped = rows.length - active.length;
  const fallback = rows.find((s) => s.isDefault && s.isActive) ?? null;

  /* خطّةٌ تشترط الموقعَ ولا موقعَ معرَّف — شرطٌ لا يُنفَّذ، ويُقال قبل أن يُكتشف. */
  const gpsPlans = active.filter((s) => s.requireGps).length;
  const noSites = summary.data ? summary.data.sites === 0 : false;

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h2 className="card-title">خطط العمل</h2>
          <span className="tiny mute">الورديّةُ ولائحةُ جزاءاتها في ورقةٍ واحدة</span>
          <span style={{ marginInlineStart: 'auto' }}>
            {canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
                <Icon name="plus" size={15} />
                خطة جديدة
              </button>
            )}
          </span>
        </div>
      </div>

      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {rows.length > 0 && (
        <div className="stats">
          <Stat
            label="خطط مفعّلة"
            value={num(active.length)}
            note={
              stopped > 0
                ? `و${num(stopped)} موقوفة لا تُختار في الملفّات الجديدة`
                : undefined
            }
          />
          <Stat
            label="موظّفون بلا خطة"
            value={summary.data ? num(summary.data.withoutSchedule) : '—'}
            note={
              summary.data
                ? 'يقعون على ساعات العمل العامّة، ولا تُطبَّق عليهم لائحةُ جزاءات'
                : 'تعذّر قراءة الملخّص'
            }
          />
          <Stat
            label="الخطة الافتراضية"
            value={fallback ? fallback.name : 'لا واحدة'}
            note={
              fallback
                ? `${fallback.startTime} — ${fallback.endTime} — هي ما يُقترح على موظّفٍ جديد`
                : 'لا خطة معلَّمة افتراضيّة، فلا شيء يُقترح على موظّفٍ جديد'
            }
          />
        </div>
      )}

      {gpsPlans > 0 && noSites && (
        <Notice title="شرطُ الموقع مكتوبٌ ولا موقعَ يُقاس عليه" icon="alert" kind="warning">
          {plural(gpsPlans, WORDS.plan)} تشترط التسجيلَ من موقع الشركة، ولا موقعَ
          مضبوطٌ في «المواقع». فيُقبل التسجيلُ من أيّ مكانٍ ويُقال ذلك للموظّف
          صراحةً — لا يُدَّعى تحقّقٌ لم يقع. أضف موقعًا ليصير الشرطُ شرطًا.
        </Notice>
      )}

      {/*
        قاعدةُ الافتراضيّة تُشرح قبل أن تُفاجئ: من يعلّم الثانيةَ افتراضيّةً
        ثمّ يجد الأولى وقد نُزعت علامتُها يظنّها عطلًا، وهي القاعدةُ نفسُها.
      */}
      <Notice title="الافتراضيةُ واحدةٌ لا أكثر" icon="star">
        تعليمُ خطّةٍ «افتراضية» ينزع العلامةَ عن سابقتها في اللحظة نفسِها —
        يفعله الخادمُ بلا سؤال. ولا يستقيم غيرُ ذلك: افتراضيّتان تعنيان أنّ
        أحدًا لا يعرف أيَّهما تُقترح على الموظّف الجديد، فيصير الاقتراحُ قرعة.
      </Notice>

      {/*
        كانت الورقةُ اليدويّةُ تقيس على ساعة الشركة والزرُّ يقيس على الخطّة،
        فكان اليومُ الواحدُ يُقرأ تأخيرًا في شاشةٍ وانضباطًا في أخرى. وصارا
        يقرآن حسابًا واحدًا، فسقط الحدُّ الذي كان مكتوبًا هنا.
      */}
      <Notice title="أين تُطبَّق هذه الخطة" icon="info">
        زرُّ الحضور في الشريط العلويّ وورقةُ الحضور التي يملؤها المسؤولُ
        بيده يقيسان كلاهما على خطّة صاحب الصفّ — تأخيرَه وانصرافَه المبكّر
        وعملَه الإضافيّ — ويقرآن عطلتَه من أيّام عملها لا من عطلة الشركة.
        وكشفُ المرتّبات يقرأ لائحةَ الجزاءات منها فيخصم بها. ومن لا خطّةَ في
        ملفّه يقع على ساعات الشركة العامّة ولا تُطبَّق عليه لائحةُ جزاءات.
      </Notice>

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا خطط بعد"
          text="بلا خطّةٍ يقع الجميعُ على ساعات العمل العامّة في إعدادات الشركة — ساعةٌ واحدةٌ للمعرض والمصنع والمناوبة الليليّة، فيُقرأ نصفُ الموظّفين متأخّرين كلَّ يوم."
          action={
            canEdit ? (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                أضف أوّل خطة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.map((s) => (
        <PlanCard key={s.id} plan={s} canEdit={canEdit} onEdit={() => setEditing(s)} />
      ))}

      {rows.length > 0 && (
        <p className="tiny mute mt-4">
          «موظّفون على هذه الخطة» يعدّ كلَّ من رُبط بها في ملفّه، بمن فيهم من
          ترك الشركة — بخلاف «موظّفون بلا خطة» أعلاه، فهو لا يعدّ إلّا من هم
          على كشف الرواتب اليوم. ولذلك لا يُجمع الرقمان في صورةٍ واحدة.
        </p>
      )}

      {editing && (
        <PlanDialog
          plan={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/* ==========================================================================
   بطاقة الخطة
   ========================================================================== */

/**
 * الخطّةُ بطاقةٌ لا صفٌّ في جدول.
 *
 * أيّامُها شكلٌ يُعرف بالنظر — خمسةٌ متّصلةٌ أم يومان في الأسبوع —
 * ولائحتُها شرائحُ لا تُضغط في خليّة. وصفٌّ في جدولٍ يجعل «0,1,2,3,4»
 * جوابًا عن سؤال «متى يعمل».
 */
function PlanCard({
  plan: s,
  canEdit,
  onEdit,
}: {
  plan: PlanRow;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">{s.name}</h2>
        {s.isDefault && <Badge kind="brand">الافتراضية</Badge>}
        {!s.isActive && <Badge kind="neutral">موقوفة</Badge>}
        {s.overnight && <Badge kind="warning">يعبر منتصف الليل</Badge>}
        <span style={{ marginInlineStart: 'auto' }}>
          {canEdit && (
            <button className="btn btn-sm" onClick={onEdit}>
              <Icon name="edit" size={14} />
              تعديل
            </button>
          )}
        </span>
      </div>

      <div className="card-body">
        <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-4)' }}>
          <DayStrip days={s.workDays} />
          <span className="num cell-strong" dir="ltr">
            {s.startTime} — {s.endTime}
          </span>
          <span className="tiny mute">
            {plural(s.workDays.length, WORDS.day)} في الأسبوع —{' '}
            <span className="num">{num(s.dailyHours)}</span> ساعة صافية
          </span>
        </div>

        <dl className="detail-list mt-4">
          <dt>ساعات الأسبوع</dt>
          <dd className="num">{num(s.weeklyHours)}</dd>

          <dt>الراحة</dt>
          <dd>
            {s.breakMinutes > 0 ? (
              <>
                <span className="num">{num(s.breakMinutes)}</span> دقيقة مخصومةٌ من
                ساعات اليوم
                {s.breakStart && s.breakEnd && (
                  <span className="tiny mute">
                    {' '}
                    — موعدُها{' '}
                    <span dir="ltr">
                      {s.breakStart} — {s.breakEnd}
                    </span>
                  </span>
                )}
              </>
            ) : (
              <span className="mute">بلا راحة مخصومة</span>
            )}
          </dd>

          <dt>سماح التأخير</dt>
          <dd>
            {s.graceMinutes > 0 ? (
              <>
                <span className="num">{num(s.graceMinutes)}</span> دقيقة قبل أن
                يُحتسب التأخير
              </>
            ) : (
              <span className="mute">بلا سماح — الدقيقةُ الأولى تأخير</span>
            )}
          </dd>

          <dt>مرونة البدء</dt>
          <dd>
            {s.flexibleMinutes > 0 ? (
              <>
                <span className="num">{num(s.flexibleMinutes)}</span> دقيقة، تُزاح
                بها نهايةُ اليوم بالمثل
              </>
            ) : (
              <span className="mute">بلا مرونة — البدايةُ ثابتة</span>
            )}
          </dd>

          <dt>خصم يوم الغياب</dt>
          <dd>
            {s.absenceDeductionDays > 0 ? (
              <>
                <span className="num">{num(s.absenceDeductionDays)}</span> من أجر
                اليوم عن كلّ يوم غياب
              </>
            ) : (
              <span className="mute">لا خصم على الغياب</span>
            )}
          </dd>

          <dt>نسيان تسجيل الانصراف</dt>
          <dd>
            {s.forgotCheckoutDays > 0 ? (
              <>
                <span className="num">{num(s.forgotCheckoutDays)}</span> من أجر
                اليوم
              </>
            ) : (
              <span className="mute">لا خصم</span>
            )}
          </dd>

          <dt>أجر الساعة الإضافية</dt>
          <dd>
            <span className="num">{num(s.overtimeRate)}</span>×{' '}
            <span className="tiny mute">من الساعة العادية</span>
          </dd>

          <dt>العمل في يوم العطلة</dt>
          <dd>
            <span className="num">{num(s.holidayRate)}</span>×{' '}
            <span className="tiny mute">من أجر اليوم</span>
          </dd>

          <dt>التسجيل</dt>
          <dd>
            <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {s.methods.map((m) => {
                const def = CHECK_METHOD_DEFS.find((d) => d.key === m);
                return (
                  <Badge key={m} kind={def?.kind ?? 'neutral'}>
                    {def?.label ?? m}
                  </Badge>
                );
              })}
              <Badge kind={s.requireGps ? 'brand' : 'neutral'}>
                {s.requireGps ? 'من موقع الشركة' : 'بلا تحقّق من المكان'}
              </Badge>
            </span>
          </dd>

          <dt>موظّفون على هذه الخطة</dt>
          <dd className="num">{s.employeeCount > 0 ? num(s.employeeCount) : '—'}</dd>
        </dl>

        {(s.lateRules.length > 0 || s.earlyRules.length > 0) && (
          <div
            className="row mt-4"
            style={{ gap: 'var(--sp-5)', flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            <RuleSummary title="لائحة التأخير" rules={s.lateRules} />
            <RuleSummary title="لائحة الانصراف المبكّر" rules={s.earlyRules} />
          </div>
        )}
      </div>
    </div>
  );
}

/** شرائحُ اللائحة مقروءةً بالجملة لا بالأرقام: «من ١٦ إلى ٣٠ دقيقة ← ربع يوم». */
function RuleSummary({ title, rules }: { title: string; rules: DeductionRule[] }) {
  if (rules.length === 0) return null;
  return (
    <div style={{ minWidth: '16rem' }}>
      <span className="label">{title}</span>
      <ul className="tiny" style={{ margin: 0, paddingInlineStart: '1.1rem' }}>
        {rules.map((r, i) => (
          <li key={i}>{ruleText(r)}</li>
        ))}
      </ul>
    </div>
  );
}

function ruleText(r: DeductionRule): string {
  const to = r.toMinutes === null ? 'فأكثر' : `حتى ${num(r.toMinutes)}`;
  return `من ${num(r.fromMinutes)} دقيقة ${to} ← خصم ${num(r.deductionDays)} من أجر اليوم`;
}

/* ==========================================================================
   شريط أيّام الأسبوع
   ========================================================================== */

/**
 * الأيّامُ مربّعاتٌ لا أرقامٌ ولا أسماءٌ مرصوصة.
 *
 * شكلُ الأسبوع هو الخبر: «خمسةٌ متّصلةٌ ثمّ راحتان» يُعرف بلمحةٍ، و«الأحد،
 * الاثنين، الثلاثاء، الأربعاء، الخميس» مكتوبةً يحتاج قراءةً وعدًّا. والمربّعُ
 * المفرَّغُ يقول «راحة» بلا كلمة.
 */
function DayStrip({
  days,
  onToggle,
}: {
  days: number[];
  /** حين تُمرَّر صارت المربّعاتُ أزرارًا تُنقر — وهو وجهُها في النموذج. */
  onToggle?: (day: number) => void;
}) {
  const on = new Set(days);

  return (
    <span className="row" style={{ gap: 4 }} role="group" aria-label="أيام العمل">
      {WEEKDAY_DEFS.map((d) => {
        const worked = on.has(d.key);
        if (!onToggle) {
          return (
            <span
              key={d.key}
              className="tiny"
              style={daySquare(worked)}
              title={worked ? `${d.label}: عمل` : `${d.label}: راحة`}
              aria-label={worked ? `${d.label}: عمل` : `${d.label}: راحة`}
            >
              {d.short}
            </span>
          );
        }
        return (
          <button
            key={d.key}
            type="button"
            className="tiny"
            style={{ ...daySquare(worked), cursor: 'pointer' }}
            onClick={() => onToggle(d.key)}
            aria-pressed={worked}
            title={d.label}
          >
            {d.short}
          </button>
        );
      })}
    </span>
  );
}

/**
 * مربّعُ اليوم مرسومًا بالرموز لا بالألوان.
 *
 * صنفٌ جديدٌ في الأنماط لهذه المربّعات وحدَها كان سيضيف اسمًا إلى ملفٍّ
 * يقرؤه كلُّ الشاشات، وهي لا تظهر إلّا هنا — فتُرسم بالرموز نفسِها التي
 * يرسم بها الملفُّ سائرَ الواجهة.
 */
function daySquare(worked: boolean): CSSProperties {
  return {
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--r-sm)',
    border: `1px solid ${worked ? 'var(--brand-100)' : 'var(--line)'}`,
    background: worked ? 'var(--brand-100)' : 'transparent',
    color: worked ? 'var(--brand-700)' : 'var(--ink-4)',
    /* المختصرُ أوّلًا: لو جاء بعد الوزن والحجم لمحاهما — وهو يُكتب لأنّ الزرّ يرث خطَّ المتصفّح لا خطَّ الصفحة. */
    font: 'inherit',
    fontSize: 'var(--fs-xs)',
    fontWeight: worked ? 600 : 400,
    lineHeight: 1,
    padding: 0,
  };
}

/* ==========================================================================
   محرّر لائحة الجزاءات
   ========================================================================== */

/**
 * اللائحةُ جدولٌ يُحرَّر لا نصٌّ يُكتب.
 *
 * «من ١٦ إلى ٣٠ ربع يوم» مكتوبةً نصًّا يحتاج من يقرؤها برنامجًا يفهم
 * العربية. وثلاثةُ حقولٍ في صفٍّ تُنتج الرقمَ نفسَه بلا تأويل — ويُقرأ
 * الصفُّ جملةً تحته ليتأكّد من كتبه أنّه كتب ما أراد.
 */
function RuleEditor({
  title,
  hint,
  rules,
  onChange,
}: {
  title: string;
  hint: string;
  rules: DeductionRule[];
  onChange: (next: DeductionRule[]) => void;
}) {
  const clash = overlappingRules(rules);

  const set = (index: number, patch: Partial<DeductionRule>) =>
    onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const add = () => {
    const last = rules[rules.length - 1];
    const from = last?.toMinutes ?? (last ? last.fromMinutes + 15 : 15);
    onChange([
      ...rules,
      { fromMinutes: from, toMinutes: from + 15, deductionDays: 0.25 },
    ]);
  };

  return (
    <div className="field span-2">
      <span className="label">{title}</span>
      <span className="tiny mute">{hint}</span>

      {rules.length === 0 ? (
        <p className="tiny mute mt-2">
          لا شرائح — فلا خصمَ من هذا الباب، ويبقى مسجَّلًا في كشف الحضور
          ليراه من يقرؤه.
        </p>
      ) : (
        <div className="table-wrap mt-2">
          <table className="table">
            <thead>
              <tr>
                <th>من (دقيقة)</th>
                <th>حتى (دقيقة)</th>
                <th>الخصم من أجر اليوم</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input
                      className="input num"
                      type="number"
                      min={0}
                      max={1440}
                      value={r.fromMinutes}
                      aria-label={`بداية الشريحة ${i + 1}`}
                      onChange={(e) =>
                        set(i, { fromMinutes: Number(e.target.value) || 0 })
                      }
                    />
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      <input
                        className="input num"
                        type="number"
                        min={1}
                        max={1441}
                        value={r.toMinutes ?? ''}
                        placeholder="مفتوحة"
                        aria-label={`نهاية الشريحة ${i + 1}`}
                        onChange={(e) =>
                          set(i, {
                            toMinutes:
                              e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                      />
                      {/*
                        الشريحةُ الأخيرةُ مفتوحةٌ عادةً: «من ساعةٍ فأكثر».
                        وتركُ الحقل فارغًا يقولها، والزرُّ يفعلها بنقرةٍ
                        لمن لا يخطر له أنّ الفراغ معنًى.
                      */}
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => set(i, { toMinutes: null })}
                        disabled={r.toMinutes === null}
                        title="اجعلها مفتوحة الطرف"
                      >
                        فأكثر
                      </button>
                    </div>
                  </td>
                  <td>
                    <input
                      className="input num"
                      type="number"
                      min={0}
                      max={30}
                      step={0.25}
                      value={r.deductionDays}
                      aria-label={`خصم الشريحة ${i + 1}`}
                      onChange={(e) =>
                        set(i, { deductionDays: Number(e.target.value) || 0 })
                      }
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => onChange(rules.filter((_, j) => j !== i))}
                      title="احذف الشريحة"
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="row mt-2" style={{ gap: 'var(--sp-3)' }}>
        <button
          type="button"
          className="btn btn-sm"
          onClick={add}
          disabled={rules.length >= MAX_DEDUCTION_RULES}
        >
          <Icon name="plus" size={14} />
          شريحة
        </button>
        {rules.length >= MAX_DEDUCTION_RULES && (
          <span className="tiny mute">
            بلغتَ الحدَّ: {num(MAX_DEDUCTION_RULES)} شرائح — لائحةٌ أطولُ لا
            يقرؤها أحد.
          </span>
        )}
      </div>

      {rules.length > 0 && (
        <ul className="tiny mute mt-2" style={{ paddingInlineStart: '1.1rem' }}>
          {rules.map((r, i) => (
            <li key={i}>{ruleText(r)}</li>
          ))}
        </ul>
      )}

      {clash && (
        <span className="tiny" style={{ color: 'var(--danger)' }}>
          الشريحتان {num(clash[0] + 1)} و{num(clash[1] + 1)} متداخلتان — تقع
          دقيقةٌ واحدةٌ في كلتيهما، فيختلف الخصمُ باختلاف الترتيب وحدَه. سيردّ
          الخادمُ الحفظ.
        </span>
      )}
    </div>
  );
}

/* ==========================================================================
   نموذج الخطة
   ========================================================================== */

function PlanDialog({ plan, onClose }: { plan: PlanRow | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState(plan?.name ?? '');
  const [days, setDays] = useState<number[]>(plan ? [...plan.workDays] : [0, 1, 2, 3, 4]);
  const [startTime, setStartTime] = useState(plan?.startTime ?? '09:00');
  const [endTime, setEndTime] = useState(plan?.endTime ?? '17:00');
  const [breakMinutes, setBreakMinutes] = useState(String(plan?.breakMinutes ?? 60));
  const [graceMinutes, setGraceMinutes] = useState(String(plan?.graceMinutes ?? 15));
  const [flexibleMinutes, setFlexibleMinutes] = useState(
    String(plan?.flexibleMinutes ?? 0),
  );
  const [breakStart, setBreakStart] = useState(plan?.breakStart ?? '');
  const [breakEnd, setBreakEnd] = useState(plan?.breakEnd ?? '');
  const [overnight, setOvernight] = useState(plan?.overnight ?? false);

  const [absenceDays, setAbsenceDays] = useState(String(plan?.absenceDeductionDays ?? 1));
  const [forgotDays, setForgotDays] = useState(String(plan?.forgotCheckoutDays ?? 0));
  const [overtimeRate, setOvertimeRate] = useState(String(plan?.overtimeRate ?? 1));
  const [holidayRate, setHolidayRate] = useState(String(plan?.holidayRate ?? 2));
  const [lateRules, setLateRules] = useState<DeductionRule[]>(
    plan ? plan.lateRules.map((r) => ({ ...r })) : [],
  );
  const [earlyRules, setEarlyRules] = useState<DeductionRule[]>(
    plan ? plan.earlyRules.map((r) => ({ ...r })) : [],
  );

  const [methods, setMethods] = useState<CheckMethod[]>(plan?.methods ?? ['WEB']);
  const [requireGps, setRequireGps] = useState(plan?.requireGps ?? true);

  const [isDefault, setIsDefault] = useState(plan?.isDefault ?? false);
  const [isActive, setIsActive] = useState(plan?.isActive ?? true);

  const toggleDay = (day: number) =>
    setDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );

  const toggleMethod = (key: CheckMethod) =>
    setMethods((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key],
    );

  const timesOk = /^\d{1,2}:\d{2}$/.test(startTime) && /^\d{1,2}:\d{2}$/.test(endTime);

  /*
   * الساعاتُ تُحسب أثناء الكتابة لا بعد الحفظ.
   *
   * ورديّةٌ راحتُها ساعتان ومداها ثمانٍ تُنتج ستًّا — وهو ما يُضرب في
   * الأيّام ويدخل في أجر الساعة والعمل الإضافيّ. ولو لم يُرَ الرقمُ هنا
   * لما ظهر الخطأُ إلّا في مسيَّر آخر الشهر، حين يصير تصحيحُه تصحيحَ
   * رواتبَ صُرفت.
   *
   * وتُحسب بدالّتي الحزمة المشتركة نفسِهما اللتين يحسب بهما الخادم: رقمان
   * من مصدرين مختلفين كانا سيختلفان يومًا ولا يُعرف أيُّهما الصحيح.
   */
  const shape = {
    workDays: days,
    startTime,
    endTime,
    breakMinutes: Number(breakMinutes) || 0,
    overnight,
  };
  const daily = timesOk ? scheduleHours(shape) : 0;
  const weekly = timesOk ? weeklyHours(shape) : 0;

  /* النهايةُ قبل البداية أو مساويةٌ لها — إمّا عبورٌ لمنتصف الليل وإمّا خطأُ إدخال. */
  const crosses = timesOk && minutesOfTime(endTime) <= minutesOfTime(startTime);
  const needsOvernight = crosses && !overnight;
  const overnightUnneeded = overnight && !crosses;
  const eatenByBreak = timesOk && daily <= 0;

  const breakHalf = (breakStart === '') !== (breakEnd === '');
  const breakBackwards =
    breakStart !== '' && breakEnd !== '' && breakEnd <= breakStart;

  const rulesClash =
    overlappingRules(lateRules) !== null || overlappingRules(earlyRules) !== null;

  const valid =
    name.trim().length >= 2 &&
    days.length > 0 &&
    timesOk &&
    !needsOvernight &&
    !eatenByBreak &&
    !breakHalf &&
    !breakBackwards &&
    !rulesClash &&
    methods.length > 0;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        workDays: [...days].sort((a, b) => a - b),
        startTime,
        endTime,
        breakMinutes: Number(breakMinutes) || 0,
        graceMinutes: Number(graceMinutes) || 0,
        flexibleMinutes: Number(flexibleMinutes) || 0,
        breakStart: breakStart === '' ? null : breakStart,
        breakEnd: breakEnd === '' ? null : breakEnd,
        overnight,
        absenceDeductionDays: Number(absenceDays) || 0,
        forgotCheckoutDays: Number(forgotDays) || 0,
        overtimeRate: Number(overtimeRate) || 0,
        holidayRate: Number(holidayRate) || 0,
        lateRules,
        earlyRules,
        methods,
        requireGps,
        isDefault,
        isActive,
      };
      return plan
        ? api(`/hr/setup/schedules/${plan.id}`, { method: 'PATCH', body })
        : api('/hr/setup/schedules', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast(plan ? 'حُفظت الخطة' : 'أُضيفت الخطة');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={plan ? `تعديل ${plan.name}` : 'خطة عمل جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="wp-name">
            اسم الخطة
          </label>
          <input
            id="wp-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="الدوام الصباحي"
            maxLength={120}
          />
          <span className="tiny mute">
            هو ما يُقرأ في ملفّ الموظّف — «خطة المعرض» أوضحُ من «خطة 2».
          </span>
        </div>

        <div className="field span-2">
          <span className="label req">أيام العمل</span>
          <DayStrip days={days} onToggle={toggleDay} />
          <span className="tiny mute">
            المربّعُ المملوءُ يومُ عمل، والمفرَّغُ راحة. وأيّامُ الراحة هنا هي
            أيّامُ هذه الخطة وحدَها، لا عطلةُ الشركة كلِّها — ومن عمل في يومٍ
            مفرَّغٍ استحقّ بدلَ العطلة أدناه.
          </span>
          {days.length === 0 && (
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              يوم عملٍ واحدٌ على الأقلّ — خطّةٌ بلا أيّامٍ ساعاتُها صفرٌ في كلّ
              أسبوع.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label req" htmlFor="wp-start">
            البداية
          </label>
          <input
            id="wp-start"
            className="input num"
            type="time"
            dir="ltr"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="wp-end">
            النهاية
          </label>
          <input
            id="wp-end"
            className="input num"
            type="time"
            dir="ltr"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-break">
            الراحة بالدقائق
          </label>
          <input
            id="wp-break"
            className="input num"
            type="number"
            min={0}
            max={480}
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(e.target.value)}
          />
          <span className="tiny mute">تُخصم من ساعات اليوم الصافية.</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-grace">
            سماح التأخير بالدقائق
          </label>
          <input
            id="wp-grace"
            className="input num"
            type="number"
            min={0}
            max={240}
            value={graceMinutes}
            onChange={(e) => setGraceMinutes(e.target.value)}
          />
          <span className="tiny mute">
            دقائقُ لا يُحتسب فيها تأخير — صفرٌ يعني أنّ الدقيقة الأولى تأخير.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-break-start">
            الراحة من
          </label>
          <input
            id="wp-break-start"
            className="input num"
            type="time"
            dir="ltr"
            value={breakStart}
            onChange={(e) => setBreakStart(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-break-end">
            الراحة إلى
          </label>
          <input
            id="wp-break-end"
            className="input num"
            type="time"
            dir="ltr"
            value={breakEnd}
            onChange={(e) => setBreakEnd(e.target.value)}
          />
          {/*
            الموعدُ يُعرض للموظّف ولا يُخصم مرّةً ثانية: المخصومُ هو حقلُ
            الدقائق أعلاه، وهذان يقولان متى — لا كم.
          */}
          <span className="tiny mute">
            موعدُ الراحة يُعرض للموظّف. والمخصومُ من ساعات اليوم هو «الراحة
            بالدقائق» وحدَه — لا يُخصم الموعدُ مرّةً ثانية.
          </span>
          {breakHalf && (
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              موعدُ الراحة يحتاج بدايةً ونهاية — أكمِل الحقلين أو أفرغهما معًا.
            </span>
          )}
          {breakBackwards && (
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              نهايةُ الراحة قبل بدايتها.
            </span>
          )}
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="wp-flex">
            مرونة البدء بالدقائق
          </label>
          <input
            id="wp-flex"
            className="input num"
            type="number"
            min={0}
            max={480}
            value={flexibleMinutes}
            onChange={(e) => setFlexibleMinutes(e.target.value)}
          />
          {/*
            المرونةُ غيرُ السماح، والخلطُ بينهما يجعل الشركةَ تتسامح مرّتين.
          */}
          <span className="tiny mute">
            غيرُ سماح التأخير: السماحُ تجاوزٌ عن تأخيرٍ يبقى تأخيرًا، والمرونةُ
            إزاحةٌ مشروعةٌ لموعد البدء — من بدأ متأخّرًا ضمنها يُنتظر منه أن
            ينصرف متأخّرًا بمثلها، فلا يُحسب له إضافيٌّ ولا يُخصم منه انصرافٌ
            مبكّر.
          </span>
        </div>

        <div className="field span-2">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={overnight}
              onChange={(e) => setOvernight(e.target.checked)}
            />
            تعبر منتصف الليل
          </label>
          {/*
            العبورُ يُعلَن ولا يُستنتج من «النهايةُ أصغر»: ورديّةٌ من التاسعة
            إلى التاسعة تبدو للاستنتاج عابرةً وهي خطأُ إدخال.
          */}
          {/*
            كان يُقال هنا إنّ العابرةَ لا يُحسب فيها إضافيٌّ ولا انصرافٌ
            مبكّر. وقد صار يُحسب: الحسابُ يبدأ من لحظة حضوره لا من ساعة
            اليوم، والانصرافُ يُغلق صفَّ أمس.
          */}
          <span className="tiny mute">
            تُعلَن ولا تُستنتج: خطّةٌ تنتهي في اليوم التالي — كالليليّة من
            العاشرة مساءً إلى السادسة صباحًا. ونهايةُ يومها تُحسب من لحظة
            حضوره، وانصرافُه صباحًا يُغلق صفَّ ليلته لا صفَّ اليوم الجديد.
          </span>
        </div>

        {/*
          الحسابُ يُعرض قبل الحفظ لا بعده.

          هذه الأرقامُ هي ما يدخل في أجر الساعة والعمل الإضافيّ، وخطؤها لا
          يظهر في شاشةٍ بل في مرتّبٍ صُرف ناقصًا.
        */}
        <div className="field span-2">
          <span className="label">ما ستُحسب به هذه الخطة</span>
          <dl className="detail-list">
            <dt>ساعات اليوم الصافية</dt>
            <dd className="num">{timesOk ? num(daily) : '—'}</dd>

            <dt>ساعات الأسبوع</dt>
            <dd>
              {timesOk && days.length > 0 ? (
                <>
                  <span className="num">{num(weekly)}</span>{' '}
                  <span className="tiny mute">
                    ({plural(days.length, WORDS.day)} في الأسبوع)
                  </span>
                </>
              ) : (
                '—'
              )}
            </dd>
          </dl>

          {needsOvernight && (
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              سيردّ الخادمُ الحفظَ ويقول: «{OVERNIGHT_SERVER_MESSAGE}» — فإمّا
              أن تعلّمها عابرةً، وإمّا أن تصحّح المواعيد.
            </span>
          )}

          {eatenByBreak && !needsOvernight && (
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              الراحةُ تساوي طولَ الوردية أو تزيد عليه، فالساعاتُ الصافية صفرٌ أو
              أقلّ — وأجرُ الساعة يُقسَّم عليها.
            </span>
          )}

          {overnightUnneeded && (
            <span className="tiny" style={{ color: 'var(--warning)' }}>
              علامةُ العبور تضيف يومًا كاملًا، ونهايةُ هذه الوردية بعد بدايتها
              في اليوم نفسِه — ولذلك تجاوزت الساعاتُ أعلاه أربعًا وعشرين. انزع
              العلامةَ إن لم تكن ليليّة.
            </span>
          )}
        </div>

        {/* ---------------- الجزاءات والبدلات ---------------- */}

        <div className="field span-2">
          <h3 className="label" style={{ marginBottom: 0 }}>
            الجزاءات والبدلات
          </h3>
          {/*
            اللائحةُ تُكتب هنا لأنّها صارت تُطبَّق: كشفُ المرتّبات يقرأ هذه
            الأرقامَ ويخصم بها. وقبلها كان التأخيرُ يُسجَّل ولا يُخصم، لأنّ
            الخصمَ سياسةٌ لم يقرّها أحدٌ داخل النظام — وهذه الورقةُ هي إقرارُها.
          */}
          <span className="tiny mute">
            ما يُكتب هنا يُطبَّق في كشف المرتّبات: يُقرأ من خطّة كلّ موظّفٍ
            ويُخصم به أو يُضاف. وما تُرك صفرًا أو فارغًا لا يُخصم منه شيء.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-absence">
            خصم يوم الغياب
          </label>
          <input
            id="wp-absence"
            className="input num"
            type="number"
            min={0}
            max={30}
            step={0.25}
            value={absenceDays}
            onChange={(e) => setAbsenceDays(e.target.value)}
          />
          <span className="tiny mute">
            بعدد أيّام الأجر: واحدٌ يعني يومًا بيوم، واثنان يعني يومين عن كلّ
            يوم غياب.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-forgot">
            خصم نسيان تسجيل الانصراف
          </label>
          <input
            id="wp-forgot"
            className="input num"
            type="number"
            min={0}
            max={30}
            step={0.25}
            value={forgotDays}
            onChange={(e) => setForgotDays(e.target.value)}
          />
          <span className="tiny mute">
            يقع على من سجّل حضورَه في يومٍ مضى ولم يسجّل انصرافَه. ويومُ اليوم
            لا يُحسب — يومٌ لم ينتهِ ليس نسيانًا.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-ot">
            أجر الساعة الإضافية
          </label>
          <input
            id="wp-ot"
            className="input num"
            type="number"
            min={0}
            max={10}
            step={0.25}
            value={overtimeRate}
            onChange={(e) => setOvertimeRate(e.target.value)}
          />
          <span className="tiny mute">
            مضروبةً في الساعة العادية: ١٫٥ تعني ساعةً ونصفًا عن كلّ ساعةٍ
            إضافيّة.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="wp-holiday">
            أجر العمل في يوم العطلة
          </label>
          <input
            id="wp-holiday"
            className="input num"
            type="number"
            min={0}
            max={10}
            step={0.25}
            value={holidayRate}
            onChange={(e) => setHolidayRate(e.target.value)}
          />
          {/*
            الراتبُ يُدفع كاملًا ثمّ يُخصم منه الغياب، فيومُ العطلة الذي عمل
            فيه مدفوعٌ أصلًا — والمضاف هو ما يزيد على الواحد.
          */}
          <span className="tiny mute">
            مضروبًا في أجر اليوم. والمضافُ فعلًا هو ما يزيد على الواحد، لأنّ
            اليومَ مدفوعٌ أصلًا في الراتب — فاثنان يعني أجرَ يومٍ إضافيًّا.
          </span>
        </div>

        <RuleEditor
          title="لائحة التأخير"
          hint="شرائحُ تُقرأ «من كذا حتى كذا» — الطرفُ الأدنى داخلٌ والأعلى خارج، فلا تقع دقيقةٌ في شريحتين. وتُقاس بعد سماح التأخير."
          rules={lateRules}
          onChange={setLateRules}
        />

        <RuleEditor
          title="لائحة الانصراف المبكّر"
          hint="تُقاس على نهاية الخطة، مزاحةً بمرونة البدء إن كانت. ومن انصرف قبلها بدقائقَ تقع في شريحةٍ يُخصم بها."
          rules={earlyRules}
          onChange={setEarlyRules}
        />

        {/* ---------------- التسجيل ---------------- */}

        <div className="field span-2">
          <span className="label req">طرق تسجيل الحضور</span>
          <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-4)' }}>
            {CHECK_METHOD_DEFS.map((d) => (
              <label
                key={d.key}
                className="row tiny"
                style={{ gap: 6, opacity: d.built ? 1 : 0.6 }}
                title={d.built ? d.description : d.missing}
              >
                <input
                  type="checkbox"
                  checked={methods.includes(d.key)}
                  disabled={!d.built}
                  onChange={() => toggleMethod(d.key)}
                />
                {d.label}
                {!d.built && <Badge kind="neutral">لم تُبنَ</Badge>}
              </label>
            ))}
          </div>
          {/*
            ما لم يُبنَ يُعرض مطفأً ويُقال لماذا: إخفاؤه يجعل من يبحث عنه
            يظنّ النظامَ ناقصًا فيسأل، وعرضُه مربّعًا يُعلَّم ثمّ لا يفعل
            شيئًا يجعله يظنّه مشتغلًا فلا يكتشف إلّا حين يشكو موظّف.
          */}
          <span className="tiny mute">
            المطفأةُ لم تُبنَ بعد وتُعرض ليُعرف أنّها ليست موجودةً — لا لتُعلَّم
            ثمّ لا تفعل شيئًا.{' '}
            {CHECK_METHOD_DEFS.filter((d) => !d.built)
              .map((d) => `${d.label}: ${d.missing}`)
              .join(' ')}
          </span>
        </div>

        <div className="field span-2">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={requireGps}
              onChange={(e) => setRequireGps(e.target.checked)}
            />
            يجب تسجيل الحضور من موقع الشركة
          </label>
          <span className="tiny mute">
            الموقعُ يُقاس على «المواقع» في هذه الصفحة، ومن سجّل من خارج
            النطاق يُردّ بالمسافة مذكورة. وبلا موقعٍ مضبوطٍ يُقبل التسجيلُ من
            أيّ مكانٍ ويُقال ذلك للموظّف في رسالةٍ عند تسجيله — لا يُدَّعى
            تحقّقٌ لم يقع. ومن تُرفع عنه العلامةُ لا يُسأل عن مكانه أصلًا،
            وهو ما يحتاجه من يعمل عن بُعدٍ أو في الميدان.
          </span>
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            الخطة الافتراضية
          </label>
          <span className="tiny mute">
            هي ما يُقترح على موظّفٍ جديد. وتعليمُها ينزع العلامةَ عن الخطة
            الافتراضية الحالية — واحدةٌ فقط تحمل الصفة.
          </span>
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعَّلة
          </label>
          {/* لا مسارَ حذفٍ في الخادم أصلًا: موظّفون وكشوفُ حضورٍ تشير إليها. */}
          <span className="tiny mute">
            الإيقافُ لا الحذف — لا حذفَ للخطط، لأنّ موظّفين وكشوفَ حضورٍ تشير
            إليها. والموقوفةُ لا تُختار في الملفّات الجديدة ويبقى من فيها عليها.
          </span>
        </div>
      </div>

      {plan && plan.employeeCount > 0 && (
        <p className="tiny mute mt-4">
          على هذه الخطة <span className="num">{num(plan.employeeCount)}</span> من
          الموظّفين. تغييرُ مواعيدها أو لائحتها يغيّر ما يُقاس به حضورُهم من
          لحظة الحفظ، ولا يعيد حسابَ أيّامٍ سُجّلت قبله ولا كشوفًا اعتُمدت.
        </p>
      )}
    </Modal>
  );
}
